import { useState } from "react";
import { useParams, useNavigate } from "react-router";
import { ArrowLeft, Check, AlertTriangle } from "lucide-react";
import { Badge } from "../../../components/shared/Badge";
import {
  usePlatformUser, usePlatformEnterprise, usePlatformRoles,
  useUnlockPlatformUser, useResetUserPasswordPlatform,
  useAssignPlatformEnterpriseRoles, useRemoveEnterpriseMembership,
  useApproveUser, useDeclineUser,
} from "@platform/hooks";
import { useToast } from "@nthamizh/ui";
import { cn } from "@nthamizh/ui";
import { rootDomain } from "../../../lib/subdomain";

interface EnterpriseRole {
  roleId: string; roleName: string; roleKey: string;
  enterpriseId: string; moduleKey: string | null;
}
interface PlatformUserDetail {
  id: string; name: string; email: string; jobTitle: string | null;
  platformRole: string | null; isLocked: boolean;
  failedLoginAttempts: number; createdAt: string;
  enterprises: { enterpriseId: string; enterpriseName: string; enterpriseSlug: string; status: string }[];
  roles: EnterpriseRole[];
}
interface PlatformRole { id: string; name: string; key: string; moduleKey: string | null }
interface Enterprise { id: string; name: string; slug: string; status: string }

export function EnterpriseUserDetailPage() {
  const { id: enterpriseId, userId } = useParams<{ id: string; userId: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();

  const { data: userData, isLoading: userLoading } = usePlatformUser(userId ?? null);
  const { data: enterprise } = usePlatformEnterprise(enterpriseId ?? null);
  const { data: rolesData = [] } = usePlatformRoles();

  const unlock = useUnlockPlatformUser();
  const resetPw = useResetUserPasswordPlatform();
  const assignRoles = useAssignPlatformEnterpriseRoles();
  const removeMembership = useRemoveEnterpriseMembership();
  const approve = useApproveUser();
  const decline = useDeclineUser();

  const user = userData as PlatformUserDetail | undefined;
  const ent = enterprise as Enterprise | undefined;
  const allRoles = rolesData as unknown as PlatformRole[];

  // Role selection state
  const [selectedRoleIds, setSelectedRoleIds] = useState<string[]>([]);
  const [rolesSynced, setRolesSynced] = useState(false);
  const [tempPassword, setTempPassword] = useState<string | null>(null);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);

  if (user && !rolesSynced) {
    const entRoles = user.roles.filter((r) => r.enterpriseId === enterpriseId).map((r) => r.roleId);
    setSelectedRoleIds(entRoles);
    setRolesSynced(true);
  }

  const membership = user?.enterprises.find((e) => e.enterpriseId === enterpriseId);

  const handleUnlock = async () => {
    if (!user) return;
    try { await unlock.mutateAsync(user.id); toast({ title: `${user.name} unlocked`, variant: "success" }); }
    catch { toast({ title: "Failed to unlock", variant: "error" }); }
  };

  const handleResetPw = async () => {
    if (!user) return;
    try { const r = await resetPw.mutateAsync(user.id); setTempPassword(r.tempPassword); }
    catch { toast({ title: "Failed to reset password", variant: "error" }); }
  };

  const handleSaveRoles = async () => {
    if (!user || !enterpriseId) return;
    try {
      await assignRoles.mutateAsync({ userId: user.id, enterpriseId, roleIds: selectedRoleIds });
      toast({ title: "Roles updated", variant: "success" });
    } catch { toast({ title: "Failed to update roles", variant: "error" }); }
  };

  const handleApprove = async () => {
    if (!user) return;
    try { await approve.mutateAsync(user.id); toast({ title: `${user.name} approved`, variant: "success" }); }
    catch { toast({ title: "Failed to approve", variant: "error" }); }
  };

  const handleDecline = async () => {
    if (!user) return;
    try { await decline.mutateAsync(user.id); toast({ title: `${user.name} declined`, variant: "success" }); }
    catch { toast({ title: "Failed to decline", variant: "error" }); }
  };

  const handleRemove = async () => {
    if (!user || !enterpriseId) return;
    try {
      await removeMembership.mutateAsync({ userId: user.id, enterpriseId });
      toast({ title: `${user.name} removed from ${ent?.name}`, variant: "success" });
      navigate(`/platform/enterprises/${enterpriseId}/users`);
    } catch { toast({ title: "Failed to remove membership", variant: "error" }); }
  };

  if (userLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-6 h-6 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="p-8 text-center text-sm text-ds-text-muted">
        User not found.{" "}
        <button onClick={() => navigate(`/platform/enterprises/${enterpriseId}/users`)} className="text-indigo-600 hover:underline">
          Back to users
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl">
      {/* Back — returns to enterprise users list */}
      <button
        onClick={() => navigate(`/platform/enterprises/${enterpriseId}/users`)}
        className="flex items-center gap-1.5 text-sm text-ds-text-secondary hover:text-ds-text transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        {ent ? `Back to ${ent.name} users` : "Back to users"}
      </button>

      {/* Hero */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-700 text-base font-semibold flex-shrink-0">
            {user.name.slice(0, 2).toUpperCase()}
          </div>
          <div>
            <h1 className="text-lg font-semibold text-ds-text">{user.name}</h1>
            <p className="text-sm text-ds-text-secondary">{user.email}</p>
            <div className="flex items-center gap-2 mt-1.5 flex-wrap">
              {membership?.status === "pending" && <Badge variant="amber">Pending approval</Badge>}
              {membership?.status === "active" && !user.isLocked && <Badge variant="green">Active</Badge>}
              {membership?.status === "inactive" && <Badge variant="gray">Inactive</Badge>}
              {user.isLocked && <Badge variant="red">Locked ({user.failedLoginAttempts} failed attempts)</Badge>}
              {user.jobTitle && <Badge variant="gray">{user.jobTitle}</Badge>}
            </div>
          </div>
        </div>
        <div className="flex gap-2 flex-shrink-0 flex-wrap justify-end">
          {membership?.status === "pending" && (
            <>
              <button onClick={handleApprove} disabled={approve.isPending}
                className="flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-emerald-50 text-emerald-700 border border-emerald-200 text-sm font-medium hover:bg-emerald-100 disabled:opacity-50">
                Approve
              </button>
              <button onClick={handleDecline} disabled={decline.isPending}
                className="flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-red-50 text-red-600 border border-red-200 text-sm font-medium hover:bg-red-100 disabled:opacity-50">
                Decline
              </button>
            </>
          )}
          {user.isLocked && (
            <button onClick={handleUnlock} disabled={unlock.isPending}
              className="flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-amber-50 text-amber-700 border border-amber-200 text-sm font-medium hover:bg-amber-100 disabled:opacity-50">
              Unlock account
            </button>
          )}
          <button onClick={handleResetPw} disabled={resetPw.isPending}
            className="flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-white border border-ds-border text-sm font-medium text-ds-text-secondary hover:bg-ds-bg disabled:opacity-50">
            Reset password
          </button>
        </div>
      </div>

      {tempPassword && (
        <div className="p-4 rounded-lg bg-amber-50 border border-amber-200">
          <p className="text-xs font-semibold text-amber-700 mb-1">Temporary password</p>
          <p className="text-sm font-mono text-amber-900">{tempPassword}</p>
          <p className="text-xs text-amber-600 mt-1">Share securely. User must change it on first login.</p>
        </div>
      )}

      {/* Enterprise context + role assignment */}
      <div className="grid grid-cols-2 gap-5">
        {/* Account info */}
        <div className="bg-white border border-ds-border rounded-card shadow-card p-5">
          <p className="text-xs font-semibold text-ds-text-muted uppercase tracking-wide mb-4">Account info</p>
          {[
            { label: "Member since", value: ent ? `${ent.name}` : "—" },
            { label: "Enterprise", value: ent ? `${ent.slug}.${rootDomain()}` : "—" },
            { label: "Membership status", value: membership?.status ?? "—" },
            { label: "Failed logins", value: user.failedLoginAttempts },
            { label: "Account locked", value: user.isLocked ? "Yes" : "No" },
          ].map(({ label, value }) => (
            <div key={label} className="flex items-center justify-between py-2.5 border-b border-ds-border last:border-0">
              <span className="text-sm text-ds-text-secondary">{label}</span>
              <span className="text-sm font-medium text-ds-text capitalize">{String(value)}</span>
            </div>
          ))}
        </div>

        {/* Role assignment */}
        <div className="bg-white border border-ds-border rounded-card shadow-card p-5">
          <p className="text-xs font-semibold text-ds-text-muted uppercase tracking-wide mb-4">
            Roles in {ent?.name ?? "enterprise"}
          </p>
          <div className="divide-y divide-ds-border mb-4">
            {allRoles.map((role) => {
              const checked = selectedRoleIds.includes(role.id);
              return (
                <button key={role.id} onClick={() =>
                  setSelectedRoleIds((prev) =>
                    prev.includes(role.id) ? prev.filter((r) => r !== role.id) : [...prev, role.id]
                  )}
                  className="w-full flex items-center gap-2.5 py-2.5 text-left hover:bg-ds-bg transition-colors"
                >
                  <div className={cn(
                    "w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 transition-colors",
                    checked ? "bg-indigo-600 border-indigo-600" : "border-ds-border-strong"
                  )}>
                    {checked && <Check className="w-2.5 h-2.5 text-white" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-ds-text">{role.name}</p>
                    {role.moduleKey && <p className="text-xs text-ds-text-muted capitalize">{role.moduleKey} module</p>}
                  </div>
                </button>
              );
            })}
          </div>
          <button onClick={handleSaveRoles} disabled={assignRoles.isPending}
            className="w-full py-2 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 disabled:opacity-50">
            {assignRoles.isPending ? "Saving…" : "Save roles"}
          </button>
        </div>
      </div>

      {/* Remove from enterprise / Danger zone */}
      <div className="bg-white border border-red-200 rounded-card shadow-card p-5">
        <h2 className="text-xs font-semibold text-red-600 uppercase tracking-wide flex items-center gap-1.5 mb-4">
          <AlertTriangle className="w-3.5 h-3.5" /> Danger zone
        </h2>
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-sm font-medium text-ds-text">Remove from enterprise</p>
            <p className="text-xs text-ds-text-muted mt-0.5">
              Revokes all roles and removes {user.name} from {ent?.name ?? "this enterprise"}. The user account is preserved.
            </p>
          </div>
          <button onClick={() => setShowRemoveConfirm((v) => !v)}
            className="flex-shrink-0 px-3.5 py-2 rounded-lg bg-red-50 text-red-600 border border-red-200 hover:bg-red-100 text-sm font-medium">
            Remove from enterprise
          </button>
        </div>
        {showRemoveConfirm && (
          <div className="mt-4 p-4 rounded-lg border border-red-200 bg-red-50 space-y-3">
            <p className="text-sm text-red-700">
              This will remove <strong>{user.name}</strong> from <strong>{ent?.name}</strong> and revoke all their roles here.
            </p>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setShowRemoveConfirm(false)}
                className="px-3 py-1.5 rounded-lg bg-white border border-ds-border text-sm text-ds-text-secondary hover:bg-ds-bg">
                Cancel
              </button>
              <button onClick={handleRemove} disabled={removeMembership.isPending}
                className="px-3 py-1.5 rounded-lg bg-red-600 text-white text-sm font-medium hover:bg-red-700 disabled:opacity-50">
                {removeMembership.isPending ? "Removing…" : "Confirm remove"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
